import { FC, useEffect, useMemo, useState } from 'react';
import clx from 'classnames';
import { BetweenChangeType, DAY_NAMES, MAX_YEAR, OLD_YEAR } from '..';
import st from './CalendarBody.module.scss';

interface ICalendarBodyProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	/** Флаг установки первого значения, в режиме диапазона */
	betweenChangeType?: BetweenChangeType;
	/** Только для чтения */
	readOnly?: boolean;
	/** Минимальная дата доступная ( по умолчанию - 01.01.1940 ) */
	minDate?: Date;
	/** Максимальная дата доступная ( по умолчанию - 31.11 текущий год + 5 ) */
	maxDate?: Date;
	/** Месяц календаря */
	month: number;
	/** Год календаря */
	year: number;

	/** Выбранные даты */
	selectedDates: Array<Date | null>;
	/** Клик по дню календаря */
	onDateClick?: (value: Date) => void;
}

/** Компонента отображающая календарь */
const CalendarBody: FC<ICalendarBodyProps> = (props) => {
	const {
		readOnly,
		betweenChangeType = 'none',
		month,
		year,
		selectedDates,
		onDateClick,
		className,
		minDate = new Date(OLD_YEAR, 0, 1),
		maxDate = new Date(MAX_YEAR, 11, 31),
		...otherProps
	} = props;

	// Массив с датами на текущий месяц + граничные ( 42 значения )
	const [calendar, setCalendar] = useState<Date[]>([]);

	/** Генерируем массив с днями  */
	useEffect(() => {
		// День недели первого дня, установленного месяца
		const firstDayThisMonth = new Date(year, month, 1).getDay();
		const temp = [];
		for (let i = 0; i < 42; i++) {
			// Онотоле Отаке !!!
			// Определяем отступ (в днях) для отображения соседних дат
			const day = getShiftDayOfFirstDayThisMonth(firstDayThisMonth);
			temp.push(new Date(year, month, i - day, 0, 0, 0, 0));
		}
		setCalendar(temp);
	}, [month, year]);

	const now = useMemo(() => {
		const tmp = new Date(Date.now());
		return new Date(
			tmp.getFullYear(),
			tmp.getMonth(),
			tmp.getDate(),
			0,
			0,
			0,
			0,
		);
	}, []);

	const onDateClickHandler = (event: any, date: Date) => {
		if (readOnly) {
			return;
		}
		if (!!event && event.nativeEvent.pointerType !== 'mouse') {
			return;
		}
		if (onDateClick) {
			onDateClick(date);
		}
	};

	/** Получение списка классов для дня ( Текущая дата, установленные даты, выходные и дни выходящий из текущего месяца ) */
	const getClassesDay = (day: Date) => {
		const isInner = day.getMonth() === month;
		const curDate = day.getTime();
		const vals = selectedDates.map((sd) => {
			return sd?.getTime() ?? 0;
		});
		return clx(st.date, {
			[st.outside]: !isInner,
			[st.current]: curDate === now.getTime(),
			[st.changed]: vals.includes(curDate),
			[st.weekend]: isInner && [6, 0].includes(day.getDay()),
		});
	};

	/** Возвращает массив с именами классов для обвертки над днем ( для подсветки дней находящихся в диапазоне ) */
	const getDayWrapperClasses = (day: Date) => {
		if (betweenChangeType === 'none' || selectedDates.length < 2) {
			return '';
		}
		const curDate = day.getTime();
		const val0 = selectedDates[0]?.getTime();
		const val1 = selectedDates[1]?.getTime();
		if (!val0 || !val1) {
			return '';
		} else if (curDate > val0 && curDate < val1) {
			return st.inBetween;
		}
	};

	/** Проверка на доступность дня */
	const isDisabledDay = (day: Date) => {
		const curDate = day.getTime();
		if (betweenChangeType !== 'none' && selectedDates.length === 2) {
			const startValue = selectedDates[0]?.getTime() ?? 0;
			const endValue = selectedDates[1]?.getTime() ?? 0;
			if (betweenChangeType === 'changeTo') {
				return !!startValue && curDate < startValue;
			} else if (endValue) {
				return endValue < curDate;
			}
		} else if (curDate < minDate.getTime() || curDate > maxDate.getTime()) {
			return true;
		}

		return false;
	};

	/** Отрисовка дня */
	const renderDay = (day: Date, index: number) => {
		return (
			<li key={index} className={getDayWrapperClasses(day)}>
				<button
					type="button"
					className={getClassesDay(day)}
					onClick={(e) => {
						return onDateClickHandler(e, day);
					}}
					disabled={isDisabledDay(day) || readOnly}
				>
					{day.getDate()}
				</button>
			</li>
		);
	};

	return (
		<div className={clx(st.body, className)} {...otherProps}>
			<ul className={st.header_days}>
				{DAY_NAMES.map((day, i) => {
					return (
						<li
							className={clx(st.header_day, {
								[st.weekend]: i > 4,
							})}
							key={day}
						>
							{day}
						</li>
					);
				})}
			</ul>
			<ul className={st.days_container}>{calendar.map(renderDay)}</ul>
		</div>
	);
};

/** Возвращает сдвиг по дням недели для отображения граничных значений */
const getShiftDayOfFirstDayThisMonth = (firstDayThisMonth: number): number => {
	return firstDayThisMonth === 0 ? 5 : firstDayThisMonth - 2;
};

export default CalendarBody;
